import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  AGRICULTURAL_RP_LOAN_PURPOSE,
  AGRICULTURAL_RP_PROPERTY_INFO,
  AGRICULTURAL_RP_PROPERTY_COST,
  AGRICULTURAL_RP_PROPERTY_DETAILS,
  AGRICULTURAL_RP_EXISTING_AG_CUSTOMER,
  AGRICULTURAL_RP_BUSINESS_INFO,
  AGRICULTURAL_RP_BUSINESS_FINANCIALS,
  AGRICULTURAL_RP_BUSINESS_OWNERS,
  AGRICULTURAL_RP_LOAN_HOLDER,
  AGRICULTURAL_RP_PERSONAL_INFO,
  AGRICULTURAL_RP_BORROWER_LIVING_SITUATION,
  AGRICULTURAL_RP_BORROWER_HOME_INFO,
  AGRICULTURAL_RP_BORROWER_RENTER_INFO,
  AGRICULTURAL_RP_BORROWER_INCOME_SOURCE,
  AGRICULTURAL_RP_BORROWER_EMPLOYMENT,
  AGRICULTURAL_RP_BORROWER_RETIREMENT,
  AGRICULTURAL_RP_EXISTING_CUSTOMER_INFO,
  AGRICULTURAL_RP_BORROWER_QUESTIONS,
  AGRICULTURAL_RP_FINAL,
} from 'app/models/sections/constants';
import { ConfirmationCommercial } from '../../components/ConfirmationCommercial';
import {
  isEmployee,
  isEmploymentOther,
  isExistingAG,
  isHomeowner,
  isRenting,
  isRetired,
  isSelfEmployed,
} from '../fields/conditionals';
import { getCommercialDeclarationQuestions } from '../questions/declarations';
import { FormTerms } from '../../components/FormAgreements/FormTerms';
import { handleAGFormSubmit, handleCommercialBackButton } from '../../actions/form/actions';
import React from 'react';
import { AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS } from '../questions/commercial-agricultural-refinance-property';
import { BorrowerType } from '@lenderful/domain';
import { ajaxValidateBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getAgriculturalRefinancePropertySectionById = (id: number): Section => {
  return AGRICULTURAL_REFINANCE_PROPERTY_SECTIONS.find((section) => section.id === id) as Section;
};

const AGRICULTURAL_REFINANCE_PROPERTY_SECTIONS: Section[] = [
  {
    id       : AGRICULTURAL_RP_LOAN_PURPOSE,
    title : 'What is the purpose of your Ag refinance?',
    questions: AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.loanPurpose,
    ]),
    agreement: <FormTerms />,
    hideBackButton: handleCommercialBackButton,
  },
  {
    id       : AGRICULTURAL_RP_PROPERTY_INFO,
    title    : 'Where is the location of the property you are refinancing?',
    questions: AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.propertyStreet,
      FieldNames.propertyStreet2,
      FieldNames.propertyCity,
      FieldNames.propertyState,
      FieldNames.propertyZip,
    ]),
  },
  {
    id       : AGRICULTURAL_RP_PROPERTY_COST,
    title    : 'Tell us more about the Ag property you are refinancing',
    questions: AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.agPropertyType,
      FieldNames.propertyValue,
      FieldNames.commercialLienBalance,
      FieldNames.loanAmount,
      FieldNames.yearTaxes,
      FieldNames.yearInsure,
    ]),
  },
  {
    id        : AGRICULTURAL_RP_PROPERTY_DETAILS,
    title     : 'Tell us more about your refinance',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.landSize,
      FieldNames.numOfStructures,
      FieldNames.buildingSquareFootage,
      FieldNames.builtYear,
    ]),
  },
  {
    id             : AGRICULTURAL_RP_EXISTING_AG_CUSTOMER,
    questions      : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.isAGExistingCustomer,
    ]),
  },
  {
    id        : AGRICULTURAL_RP_BUSINESS_INFO,
    title     : 'Tell us about your business',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.commercialPhone,
      FieldNames.commercialEmployeeCount,
      FieldNames.commercialEntityType,
      FieldNames.commercialYearStarted,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_BUSINESS_FINANCIALS,
    title     : 'Tell us about your business financials',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.commercialRevenueTrend3,
      FieldNames.commercialRevenueLastYear,
      FieldNames.commercialRevenueExplanation,
      FieldNames.commercialProfitTrend3,
      FieldNames.commercialProfitExplanation,
      FieldNames.commercialCollateralList,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_BUSINESS_OWNERS,
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.commercialOwnerList,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_LOAN_HOLDER,
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.commercialLoanHolder,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id       : AGRICULTURAL_RP_PERSONAL_INFO ,
    title    : 'Enter your personal information',
    questions: AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_BORROWER_LIVING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([FieldNames.livingRentOrOwn]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id       : AGRICULTURAL_RP_BORROWER_HOME_INFO,
    title    : 'Provide your homeowner information',
    questions: AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
    ]),
    showIf: [(values) => isHomeowner(values) && !isExistingAG(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : AGRICULTURAL_RP_BORROWER_RENTER_INFO,
    title     : 'Provide your renter information',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
    ]),
    showIf : [(values) => isRenting(values) && !isExistingAG(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : AGRICULTURAL_RP_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([FieldNames.employmentType]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_BORROWER_EMPLOYMENT,
    title     : 'Tell us about your income and employment',
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.commercialEmploySameBusiness,
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf       : [(values) => (isEmployee(values) || isSelfEmployed(values)) && !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_RP_BORROWER_RETIREMENT,
    questions : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf       : [(values) => (isRetired(values) ||  isEmploymentOther(values)) && !isExistingAG(values)],
  },
  {
    id           : AGRICULTURAL_RP_EXISTING_CUSTOMER_INFO,
    title        : 'Please enter your Loan and Business Information',
    questions    : AGRICULTURAL_REFINANCE_PROPERTY_QUESTIONS.getByName([
      FieldNames.existingLoanNumber,
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.firstName,
      FieldNames.lastName,
      FieldNames.email,
      FieldNames.phone,
    ]),
    showIf       : [isExistingAG],
    submitAction : handleAGFormSubmit,
    submitText   : 'Submit',
  },
  {
    id           : AGRICULTURAL_RP_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getCommercialDeclarationQuestions(BorrowerType.PRIMARY),
    showIf: [(values) => !isExistingAG(values)],
    submitAction : handleAGFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id              : AGRICULTURAL_RP_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
